/**
 * API client.
 *
 * One place owns the bearer token, the error shape and the SSE decoding, so a
 * component never touches fetch directly.
 */

import type {
  AgentEvent,
  ApiError,
  AssetIntelligence,
  AssetSummary,
  BlockingHandoff,
  BriefResponse,
  DataHealth,
  HealthResponse,
  MeResponse,
  PlanComparison,
  PlannerUserOut,
  PlanResponse,
  PlanVersionOut,
  RevalidationResult,
  RunResponse,
  ScoringConfig,
  ScoringConfigDefaults,
  SessionResponse,
  TenantOut,
} from './types'

const TOKEN_KEY = 'captura_planner_token'
const API_BASE_URL = (import.meta.env.VITE_API_TARGET ?? '').replace(/\/$/, '')
//: Only meaningful for a superadmin - see resolve_principal. Sending this
//: header for any other role is harmless: their own token's tenant claim
//: always wins (app/core/security.py resolve_principal).
const TENANT_OVERRIDE_KEY = 'captura_planner_tenant_override'

export function getToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY)
  } catch {
    return null
  }
}

export function setToken(token: string | null): void {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token)
    else localStorage.removeItem(TOKEN_KEY)
  } catch {
    /* private browsing; the session simply will not persist */
  }
}

export function getTenantOverride(): string | null {
  try {
    return localStorage.getItem(TENANT_OVERRIDE_KEY)
  } catch {
    return null
  }
}

export function setTenantOverride(tenantId: string | null): void {
  try {
    if (tenantId) localStorage.setItem(TENANT_OVERRIDE_KEY, tenantId)
    else localStorage.removeItem(TENANT_OVERRIDE_KEY)
  } catch {
    /* private browsing; the override simply will not persist */
  }
}

export class RequestFailed extends Error {
  constructor(
    readonly status: number,
    readonly error: ApiError,
  ) {
    super(error.message)
    this.name = 'RequestFailed'
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = getToken()
  const tenantOverride = getTenantOverride()
  const response = await fetch(`${API_BASE_URL}/api/v1${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(tenantOverride ? { 'X-Tenant-Id': tenantOverride } : {}),
      ...(init.headers ?? {}),
    },
  })
  if (response.status === 204) return undefined as T
  const text = await response.text()
  const payload = text ? JSON.parse(text) : {}
  if (!response.ok) {
    const error: ApiError = payload?.error ?? {
      code: 'request_failed',
      message: `Request failed with status ${response.status}.`,
    }
    throw new RequestFailed(response.status, error)
  }
  return payload as T
}

const post = <T>(path: string, body?: unknown) =>
  request<T>(path, { method: 'POST', body: body === undefined ? undefined : JSON.stringify(body) })
const put = <T>(path: string, body: unknown) =>
  request<T>(path, { method: 'PUT', body: JSON.stringify(body) })
const patch = <T>(path: string, body: unknown) =>
  request<T>(path, { method: 'PATCH', body: JSON.stringify(body) })

export const api = {
  // -- auth --------------------------------------------------------------
  login: (email: string, password: string) =>
    post<{ access_token: string; email: string; tenant_id: string; role: string }>(
      '/auth/login',
      { email, password },
    ),
  devToken: (email: string, tenantId?: string, role?: string) =>
    post<{ access_token: string; email: string; tenant_id: string; role: string }>(
      '/auth/dev-token',
      { email, tenant_id: tenantId ?? null, role: role ?? 'campaign_planner' },
    ),
  me: () => request<MeResponse>('/auth/me'),
  health: () => request<HealthResponse>('/health'),

  // -- briefs ------------------------------------------------------------
  parseBrief: (text: string, sessionId?: string) =>
    post<BriefResponse>('/briefs', { text, session_id: sessionId ?? null }),
  getBrief: (id: string) => request<BriefResponse>(`/briefs/${id}`),
  updateBrief: (id: string, changes: Record<string, unknown>, reason?: string) =>
    put<BriefResponse>(`/briefs/${id}`, { changes, reason: reason ?? null }),
  markFlexible: (id: string, fields: string[]) =>
    post<BriefResponse>(`/briefs/${id}/flexible`, { fields }),
  validateBrief: (id: string) => post<BriefResponse>(`/briefs/${id}/validate`),
  briefVersions: (id: string) =>
    request<{ version: number; change_summary: string; created_at: string }[]>(
      `/briefs/${id}/versions`,
    ),

  // -- runs --------------------------------------------------------------
  createRun: (body: {
    brief_id: string
    session_id?: string | null
    objective?: string
    radius_m?: number | null
    locked_asset_ids?: string[]
    excluded_asset_ids?: string[]
    top_n?: number
  }) => post<RunResponse>('/recommendation-runs', body),
  getRun: (id: string, topN = 50) => request<RunResponse>(`/recommendation-runs/${id}?top_n=${topN}`),
  listRuns: (briefId?: string) =>
    request<Record<string, unknown>[]>(
      `/recommendation-runs${briefId ? `?brief_id=${briefId}` : ''}`,
    ),

  // -- plans -------------------------------------------------------------
  createVersion: (body: {
    run_id: string
    plan_id?: string | null
    name?: string | null
    label?: string | null
    captura_asset_ids?: string[] | null
    locked_asset_ids?: string[]
    change_summary?: string | null
  }) => post<PlanVersionOut>('/plans/versions', body),
  listPlans: () => request<PlanResponse[]>('/plans'),
  getPlan: (id: string) => request<PlanResponse>(`/plans/${id}`),
  getVersion: (planId: string, version: number) =>
    request<PlanVersionOut>(`/plans/${planId}/versions/${version}`),
  editVersion: (
    planId: string,
    body: {
      base_version?: number | null
      add_asset_ids?: string[]
      remove_asset_ids?: string[]
      lock_asset_ids?: string[]
      unlock_asset_ids?: string[]
      label?: string | null
      change_summary?: string | null
    },
  ) => post<PlanVersionOut>(`/plans/${planId}/edit`, body),
  compare: (planId: string, versions: number[]) =>
    post<PlanComparison>(`/plans/${planId}/compare`, { versions }),
  submitPlan: (planId: string, version?: number, comments?: string) =>
    post<Record<string, unknown>>(`/plans/${planId}/submit`, {
      version: version ?? null,
      comments: comments ?? null,
    }),
  decide: (
    planId: string,
    body: {
      version?: number | null
      approve: boolean
      comments?: string | null
      acknowledged_assumptions?: string[]
    },
  ) => post<Record<string, unknown>>(`/plans/${planId}/decision`, body),
  approvals: (planId: string) =>
    request<Record<string, unknown>[]>(`/plans/${planId}/approvals`),
  revalidate: (planId: string, version?: number) =>
    post<RevalidationResult>(
      `/plans/${planId}/revalidate${version ? `?version=${version}` : ''}`,
    ),

  // -- campaigns ---------------------------------------------------------
  createCampaign: (body: {
    plan_id: string
    version: number
    account_id?: number | null
    brand_id?: number | null
    outdoor_media_type?: number | null
    accept_price_changes?: boolean
  }) => post<Record<string, any>>('/campaigns', body),
  getHandoff: (planId: string, version: number) =>
    request<Record<string, any>>(`/campaigns/handoffs/${planId}/${version}`),

  // -- blockings -----------------------------------------------------------
  // Captura now exposes a direct, token-authenticated create endpoint
  // (createBlocking); the file/manual-confirm path below remains for a
  // deployment with no Captura API configured.
  createBlocking: (body: {
    plan_id: string
    version: number
    expected_campaign_name?: string | null
    package_type?: string
    foc_days?: number
    digital_slot?: string | null
    accept_price_changes?: boolean
  }) => post<BlockingHandoff>('/blockings/create', body),
  downloadBlockingFile: async (body: {
    plan_id: string
    version: number
    expected_campaign_name?: string | null
    package_type?: string
    wrap_type?: string | null
    foc_days?: number
    digital_slot?: string | null
    agency_commission_pct?: number
  }): Promise<{ blob: Blob; filename: string }> => {
    const token = getToken()
    const tenantOverride = getTenantOverride()
    const response = await fetch(`${API_BASE_URL}/api/v1/blockings/download`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(tenantOverride ? { 'X-Tenant-Id': tenantOverride } : {}),
      },
      body: JSON.stringify(body),
    })
    if (!response.ok) {
      const text = await response.text()
      const payload = text ? JSON.parse(text) : {}
      const error: ApiError = payload?.error ?? {
        code: 'request_failed',
        message: `Request failed with status ${response.status}.`,
      }
      throw new RequestFailed(response.status, error)
    }
    const disposition = response.headers.get('content-disposition') ?? ''
    const filename = /filename="([^"]+)"/.exec(disposition)?.[1] ?? 'blocking.xlsx'
    return { blob: await response.blob(), filename }
  },
  // Manual confirmation that a downloaded blocking file was uploaded and the
  // booking created in Captura by hand - there is no API to detect this yet.
  getBlockingStatus: (planId: string, version: number) =>
    request<BlockingHandoff>(`/blockings/status/${planId}/${version}`),
  markBookingCreated: (body: { plan_id: string; version: number; captura_campaign_id?: string | null; notes?: string | null }) =>
    post<BlockingHandoff>('/blockings/mark-booking-created', body),

  // -- assets ------------------------------------------------------------
  cities: (q?: string) =>
    request<{ id: number | null; name: string }[]>(`/assets/cities${q ? `?q=${q}` : ''}`),
  searchAssets: (body: Record<string, unknown>) => post<AssetSummary[]>('/assets/search', body),
  intelligence: (assetId: string, briefId?: string, radius?: number) => {
    const params = new URLSearchParams()
    if (briefId) params.set('brief_id', briefId)
    if (radius) params.set('radius_m', String(radius))
    const qs = params.toString()
    return request<AssetIntelligence>(`/assets/${assetId}/intelligence${qs ? `?${qs}` : ''}`)
  },

  // -- planner sessions --------------------------------------------------
  createSession: (briefId?: string, planId?: string) =>
    post<SessionResponse>('/planner/sessions', {
      brief_id: briefId ?? null,
      plan_id: planId ?? null,
    }),
  getSession: (id: string) => request<SessionResponse>(`/planner/sessions/${id}`),
  messages: (id: string) =>
    request<
      { sequence: number; role: string; content: string | null; tool_calls: any[]; created_at: string }[]
    >(`/planner/sessions/${id}/messages`),
  traces: (id: string) => request<Record<string, unknown>[]>(`/planner/sessions/${id}/traces`),

  // -- admin -------------------------------------------------------------
  dataHealth: () => request<DataHealth>('/admin/data-health'),
  scoringConfigs: () => request<ScoringConfig[]>('/admin/scoring-configs'),
  scoringConfigDefaults: () => request<ScoringConfigDefaults>('/admin/scoring-configs/defaults'),
  createScoringConfig: (body: {
    scoring_version: string
    objective?: string
    weights: Record<string, number>
    penalties?: Record<string, number> | null
    missing_feature_policy?: string
    notes?: string | null
  }) => post<ScoringConfig & { note: string }>('/admin/scoring-configs', body),
  updateScoringConfig: (id: string, body: { is_active?: boolean; notes?: string | null }) =>
    patch<ScoringConfig>(`/admin/scoring-configs/${id}`, body),
  auditLog: (objectType?: string, objectId?: string) => {
    const params = new URLSearchParams()
    if (objectType) params.set('object_type', objectType)
    if (objectId) params.set('object_id', objectId)
    const qs = params.toString()
    return request<Record<string, unknown>[]>(`/admin/audit${qs ? `?${qs}` : ''}`)
  },

  // -- user management (admin, within the current tenant) ----------------
  users: () => request<PlannerUserOut[]>('/admin/users'),
  createUser: (body: { email: string; display_name?: string | null; role: string }) =>
    post<PlannerUserOut>('/admin/users', body),
  updateUser: (
    id: string,
    body: { display_name?: string | null; role?: string; is_active?: boolean },
  ) => patch<PlannerUserOut>(`/admin/users/${id}`, body),

  // -- tenant management (superadmin only) --------------------------------
  tenants: () => request<TenantOut[]>('/admin/tenants'),
  createTenant: (body: {
    id: string
    name: string
    default_currency?: string
    is_synthetic?: boolean
  }) => post<TenantOut>('/admin/tenants', body),
}

/** Trigger a browser save of a blob fetched from the API (e.g. the blocking file). */
export function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}

/**
 * Stream one agent turn.
 *
 * Uses fetch rather than EventSource because the request needs an
 * Authorization header and a POST body, neither of which EventSource supports.
 */
export async function streamAgentTurn(
  sessionId: string,
  message: string,
  options: {
    confirm?: boolean
    onEvent: (event: AgentEvent) => void
    signal?: AbortSignal
  },
): Promise<void> {
  const token = getToken()
  const tenantOverride = getTenantOverride()
  const response = await fetch(`${API_BASE_URL}/api/v1/planner/sessions/${sessionId}/messages`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(tenantOverride ? { 'X-Tenant-Id': tenantOverride } : {}),
    },
    body: JSON.stringify({ message, confirm: options.confirm ?? false, stream: true }),
    signal: options.signal,
  })

  if (!response.ok || !response.body) {
    const text = await response.text().catch(() => '')
    let error: ApiError = { code: 'agent_failed', message: 'The assistant could not be reached.' }
    try {
      error = JSON.parse(text)?.error ?? error
    } catch {
      /* keep the fallback */
    }
    throw new RequestFailed(response.status, error)
  }

  const reader = response.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''

  const consumeFrame = (frame: string) => {
    let eventName = 'message'
    const dataLines: string[] = []
    for (const line of frame.split(/\r\n|\n/)) {
      if (line.startsWith('event:')) eventName = line.slice(6).trim()
      else if (line.startsWith('data:')) dataLines.push(line.slice(5).trim())
    }
    if (!dataLines.length) return
    try {
      options.onEvent({
        type: eventName as AgentEvent['type'],
        data: JSON.parse(dataLines.join('\n')),
      })
    } catch {
      /* a frame we cannot parse is skipped rather than killing the stream */
    }
  }

  for (; ;) {
    const { done, value } = await reader.read()
    if (done) break
    buffer += decoder.decode(value, { stream: true })

    // SSE frames are separated by a blank line. sse-starlette emits CRLF
    // ("\r\n\r\n") by default, so the split must not assume bare "\n\n".
    // Anything after the last blank line is a partial frame and stays in
    // the buffer.
    const frames = buffer.split(/\r\n\r\n|\n\n/)
    buffer = frames.pop() ?? ''

    for (const frame of frames) consumeFrame(frame)
  }

  // Flush a final frame that arrived without a trailing blank line.
  if (buffer.trim()) consumeFrame(buffer)
}
